import { createTheme, Theme } from '@mui/material/styles';

declare module '@mui/styles' {
  interface DefaultTheme extends Theme {}
}

export const commonTheme = createTheme({
  components: {
    MuiFab: {
      styleOverrides: {
        sizeSmall: {
          height: 24,
          minHeight: 24,
          width: 24,
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          width: '100%',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        contained: {
          margin: '.25rem .75rem 0',
        },
        root: {
          '&.Mui-error': {
            color: '#bd2e2a',
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: {
          color: '#bd2e2a',
        },
        root: {
          color: '#62695e',
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: 1.5,
          lineHeight: 1.67,
          textTransform: 'uppercase',
        },
      },
    },
    MuiIconButton: {
      defaultProps: { 'aria-label': 'button' },
      styleOverrides: {
        root: {
          color: 'inherit',
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          outline: 'revert',
          '&:visited': {
            outline: '2px solid #007bbd',
          },
          '&:focus': {
            outline: '2px solid #007bbd',
          },
          '&:focus-visible': {
            outline: '2px solid #007bbd',
          },
          '.site-ja': {
            '&:visited': {
              outline: '2px solid #00aea9',
            },
            '&:focus': {
              outline: '2px solid #00aea9',
            },
            '&:focus-visible': {
              outline: '2px solid #00aea9',
            },
          },
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        positionEnd: {
          color: '#373737',
        },
        root: {
          color: '#82bc00',
          '.site-ja &': {
            color: '#00aea9',
          },
        },
      },
    },
    MuiInputBase: {
      defaultProps: { 'aria-label': 'input' },
      styleOverrides: {
        formControl: {
          'label + &': {
            marginTop: 20,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        shrink: {
          transform: 'unset',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderBottom: '0.03125rem solid #dedede',
          color: '#373737',
          fontFamily: ['"Open Sans"', '"Helvetica Neue"', 'Arial', 'sans-serif'].join(','),
          padding: '0.625rem 1rem',
          whiteSpace: 'normal',
          '&:last-child': {
            borderBottom: 0,
          },
          '&.Mui-focused': {
            background: '#fff !important',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        adornedStart: {
          paddingLeft: 0,
        },
        input: {
          backgroundColor: 'transparent',
          color: '#373737',
          '.Mui-error &': {
            color: '#bd2e2a',
          },
          '.Mui-error.Mui-focused &': {
            color: '#373737',
          },
          padding: '0.5rem 0.25rem',
        },
        notchedOutline: {
          border: 0,
          '&:hover': {
            border: '2px solid #007bbd',
          },
        },
        root: {
          backgroundColor: '#fff',
          border: '1px solid #dedede',
          borderRadius: 12,
          boxShadow: 'inset 0 1px 3px 0 #00000014',
          color: '#373737',
          fontFamily: ['"Open Sans"', '"Helvetica Neue"', 'Arial', 'sans-serif'].join(','),
          height: '58px',
          padding: 0,
          '&.MuiInputBase-adornedStart': {
            paddingLeft: '15px',
          },
          '&.MuiInputBase-multiline': {
            height: 'auto',
            minHeight: '58px',
          },
          '&:hover': {
            '.site-ja &': {
              borderColor: '#00838f',
            },
          },
          '&.MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
            padding: '0.5625rem',
            paddingRight: '0.875rem !important',
          },
          '&.Mui-focused': {
            border: '2px solid #007bbd',
            '& > input.MuiOutlinedInput-input': {
              height: '95%',
              padding: '0 0.5rem',
            },
            '.site-ja &': {
              borderColor: '#00838f',
            },
            '[aria-expanded="true"] &': {
              '& > .MuiInputAdornment-positionEnd': {
                color: '#007bbd',
                transform: 'rotate(180deg)',
                '.site-ja &': {
                  color: '#00838f',
                },
              },
            },
          },
        },
      },
    },
    MuiPagination: {
      styleOverrides: {
        root: {
          display: 'flex',
          justifyContent: 'center',
          '& .MuiPaginationItem-page': {
            color: '#373737',
            fontSize: 12,
            '&.Mui-selected': {
              background: '#fff',
              color: '#663573',
            },
          },
        },
      },
    },
    // @ts-ignore
    MuiPickersDay: {
      stylesOverrides: {
        current: {
          color: '#007bbd',
          '.site-ja &': {
            color: '#00748c',
          },
        },
        daySelected: {
          backgroundColor: '#007bbd',
          '.site-ja &': {
            backgroundColor: '#00748c',
          },
          '&:hover': {
            backgroundColor: '#0a6799',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          '&:focus': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
  },

  typography: {
    fontFamily: ['"Open Sans"', '"Helvetica Neue"', 'Arial', 'sans-serif'].join(','),
    button: {
      textTransform: 'none',
    },
  },
});
